import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { HeaderTabsFit } from './page-tabs';

/**
 * PageHeader — the ONE page header (Z4) for every practice screen, the same element as the
 * practitioner app's ContextHeader. Antonin, 13 Oct: "one single element … cleaner, consistent,
 * more compact"; settled as "small title, more air".
 *
 * - One row, `--page-header-height` (80) tall, `--page-header-pad` (16) on all four sides.
 * - LEFT is context: an optional 48px photo (`leading`), the title on the header-title role
 *   (24/32/600, the title role, body ink) with an optional quiet `tag` beside it, over at most one subline
 *   (`meta`, 13/20 secondary ink).
 * - RIGHT is what acts on the page: the `tabs` first (underline tabs whose 2px underline lies on
 *   the header's bottom hairline; what does not fit goes into More), then the `actions`, the one
 *   primary action last. Both sit on the subline's bottom line, 16 from the hairline.
 * - One hairline under it, drawn inset so it never adds a pixel to the 80.
 *
 * Structure only: it renders what it is handed and owns no state.
 */
export interface PageHeaderProps {
  title: ReactNode;
  /** The subline: a count or the page's one-line description. */
  meta?: ReactNode;
  /** A quiet tag after the title (see PageTag). */
  tag?: ReactNode;
  /** Filters and actions on the right, after the tabs; the primary action last. */
  actions?: ReactNode;
  /** The page's tabs (PageTabs, or links styled with `pageTabClass`), first in the right cluster. */
  tabs?: ReactNode;
  /** Before the title: a 48px photo when the page is a person. */
  leading?: ReactNode;
  /** A back control on the title's line, just before the title (a detail page reached from a list). */
  back?: ReactNode;
  className?: string;
}

export function PageHeader({ title, meta, tag, actions, tabs, leading, back, className }: PageHeaderProps) {
  return (
    <header
      data-page-header
      className={cn(
        'flex h-[var(--page-header-height)] shrink-0 items-center gap-6 px-[var(--page-header-pad)] shadow-[var(--page-header-hairline)]',
        className,
      )}
    >
      {/* Context sits on the top line (12, the subline ends on the 16-from-the-bottom line), never centred: a title without a subline sits where every
          title does (Antonin, 13 Oct). */}
      <div data-page-context className="flex min-w-[var(--page-header-context-min)] shrink items-end gap-3 self-start pt-[var(--page-header-context-top)]">
        {leading != null ? (
          <div data-page-leading className="shrink-0">
            {leading}
          </div>
        ) : null}
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-3">
            {back != null ? (
              <div data-page-back className="shrink-0">
                {back}
              </div>
            ) : null}
            {/* A title is never cut by its tag: the tag gives way (13 Oct). */}
            <h1 className={cn('text-[var(--ink-body)] [font:var(--type-header-title)]', tag ? 'shrink-0 whitespace-nowrap' : 'truncate')}>
              {title}
            </h1>
            {tag}
          </div>
          {meta != null && meta !== '' ? (
            <p data-page-meta className="truncate text-[var(--ink-secondary)] [font:var(--type-body)]">
              {meta}
            </p>
          ) : null}
        </div>
      </div>
      {tabs != null ? (
        <HeaderTabsFit>
          <div data-testid="page-header-tabs" className="flex h-full items-stretch">
            {tabs}
          </div>
        </HeaderTabsFit>
      ) : (
        <div className="flex-1" />
      )}
      {actions != null ? (
        <div data-page-actions className="mb-[var(--page-header-pad)] flex shrink-0 items-center gap-2 self-end">
          {actions}
        </div>
      ) : null}
    </header>
  );
}

/** A quiet tag beside a page title: hairline outline, secondary ink, no fill (colour is for clinical meaning). */
export function PageTag({ children }: { children: ReactNode }) {
  return (
    <span className="self-center rounded-tag border border-[var(--surface-line)] px-2 text-xs font-medium text-[var(--ink-secondary)]">
      {children}
    </span>
  );
}
